import React, { Component } from 'react';
import './filter.select.scss';
import { ELSIcon } from '../els';

const ALL_OPTION = 'All';

type SelectValue = string | string[];

interface SelectProps {
  label: string;
  options: string[];
  value: SelectValue;
  isMulti: boolean;
  showSelectAll?: boolean;
  onChange: (value: SelectValue) => void;
}

interface SelectState {
  isOpen: boolean;
  activeIndex: number;
}

class FilterSelect extends Component<SelectProps, SelectState> {
  private containerRef: React.RefObject<HTMLDivElement>;

  constructor(props: SelectProps) {
    super(props);
    this.state = {
      isOpen: false,
      activeIndex: -1
    };
    this.containerRef = React.createRef<HTMLDivElement>();
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  handleClickOutside = (event: MouseEvent) => {
    if (this.containerRef.current && !this.containerRef.current.contains(event.target as Node)) {
      this.closeDropdown();
    }
  };

  getTotalOptions = (): number => {
    const { options, showSelectAll } = this.props;

    return showSelectAll ? options.length + 1 : options.length;
  };

  openDropdown = () => {
    const totalOptions = this.getTotalOptions();

    this.setState({ isOpen: true, activeIndex: totalOptions > 0 ? 0 : -1 });
  };

  handleActiveSelection = (activeIndex: number) => {
    const { options, showSelectAll } = this.props;

    if (activeIndex < 0) {
      return;
    }

    if (showSelectAll && activeIndex === 0) {
      this.handleSelect(ALL_OPTION, true);
      return;
    }

    const optionIndex = showSelectAll ? activeIndex - 1 : activeIndex;
    this.handleSelect(options[optionIndex]);
  };

  handleKeyDown = (e: React.KeyboardEvent) => {
    const { isOpen, activeIndex } = this.state;
    const totalOptions = this.getTotalOptions();

    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        this.openDropdown();
      }
      return;
    }

    if (totalOptions === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.setState({ activeIndex: (activeIndex + 1) % totalOptions });
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.setState({
          activeIndex: (activeIndex - 1 + totalOptions) % totalOptions
        });
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.handleActiveSelection(activeIndex);
        break;
      case 'Escape':
      case 'Tab':
        this.closeDropdown();
        break;
      default:
        break;
    }
  };

  handleSelect = (option: string, isSelectAll?: boolean) => {
    const { isMulti, value, onChange, options } = this.props;

    if (isSelectAll) {
      if (isMulti && Array.isArray(value)) {
        const newValue = value.filter((item) => item !== ALL_OPTION).length === options.length ? [] : [ALL_OPTION, ...options];
        onChange(newValue);
      }
      return;
    }

    if (isMulti && Array.isArray(value)) {
      let newValue = [...value, option];
      if (value.includes(option)) {
        newValue = value.filter((item) => item !== option);
      }
      const selectedValues = newValue.filter((item) => item !== ALL_OPTION);

      if (selectedValues.length === options.length) {
        onChange([ALL_OPTION, ...options]);
        return;
      }

      onChange(selectedValues);
    } else {
      onChange(option);
      this.closeDropdown();
    }
  };

  getIsSelected = (option: string): boolean => {
    const { value, isMulti, options } = this.props;

    if (isMulti && Array.isArray(value)) {
      if (option === ALL_OPTION) {
        return value.filter((item) => item !== ALL_OPTION).length === options.length;
      }
      return value.includes(option);
    }

    return value === option;
  };

  getDisplayCount = (): number => {
    const { options, value, isMulti, showSelectAll } = this.props;

    if (isMulti && Array.isArray(value)) {
      return value.length === options.length && showSelectAll ? value.length + 1 : value.length;
    }

    return value ? 1 : 0;
  };

  closeDropdown = () => {
    this.setState({ isOpen: false, activeIndex: -1 });
  };

  renderOption = (option: string, index: number) => {
    const { activeIndex } = this.state;
    const isSelected = this.getIsSelected(option);
    const isActive = index === activeIndex;
    const isAllOption = option === ALL_OPTION;

    return (
      <div
        key={option}
        className={`select-option ${isActive ? 'active-focus' : ''}`}
        onClick={() => this.handleSelect(option, isAllOption)}
        onMouseEnter={() => this.setState({ activeIndex: -1 })}
        role="option"
        aria-selected={isSelected}
        tabIndex={-1}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.handleSelect(option, isAllOption);
          }
        }}
      >
        <div className="checkbox-wrapper">
          <div className={`custom-box ${isSelected ? 'checked' : ''}`}>{isSelected && <ELSIcon className="tick" color="white" name="checkmark" size="1o2" />}</div>
        </div>
        <span className="option-text">{option}</span>
      </div>
    );
  };

  render() {
    const { isOpen } = this.state;
    const { label, options, showSelectAll, isMulti } = this.props;
    const displayCount = this.getDisplayCount();

    return (
      <div className="custom-select-container" ref={this.containerRef}>
        <div
          className="select-header"
          onClick={() => this.setState({ isOpen: !isOpen })}
          onKeyDown={this.handleKeyDown}
          tabIndex={0}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls="results-listbox"
        >
          <div className="header-left">
            <span className="select-label">{label}</span>
            <span className="select-badge">{displayCount}</span>
          </div>
          <div className="icon-stack">
            <div className={`icon-layer ${isOpen ? 'hide' : 'show'}`}>
              <ELSIcon color="secondary" name="chevron-down" size="1x" />
            </div>
            <div className={`icon-layer ${isOpen ? 'show' : 'hide'}`}>
              <ELSIcon color="secondary" name="chevron-up" size="1x" />
            </div>
          </div>
        </div>

        {isOpen && (
          <div className="select-dropdown" id="results-listbox" role="listbox" aria-multiselectable={isMulti}>
            {showSelectAll && this.renderOption(ALL_OPTION, 0)}
            {options.map((opt, idx) => this.renderOption(opt, showSelectAll ? idx + 1 : idx))}
          </div>
        )}
      </div>
    );
  }
}

export default FilterSelect;
